import { compact, flatMap, sortBy, toPairs } from 'lodash';

import {
  isNestedReportJobResult,
  RenderableReportJobResult,
  ReportJobResultGroup,
  ReportJobResults,
} from 'interfaces/job';
import { humanize } from 'utils/helpers';

export interface ReportJobDisplaySection {
  id: string;
  title: string;
  resultGroup: ReportJobResultGroup;
  sourceGroupName: string;
  isExpandedFromWrapper: boolean;
  navigationParentId?: string;
  navigationParentTitle?: string;
  rowGroupId?: string;
  rowColumns?: number;
  nestedSectionControl?: ReportJobDisplaySectionNestedControl;
}

export interface ReportJobDisplaySectionNestedControl {
  groupId: string;
  combineMode: ReportJobNestedSectionCombineMode;
  layoutMode: ReportJobNestedSectionLayoutMode;
}

export type ReportJobNestedSectionCombineMode = 'combined' | 'unwound';
export type ReportJobNestedSectionLayoutMode = 'one-up' | 'two-up';

interface BuildReportJobDisplaySectionsOptions {
  nestedSectionCombineModeByGroupId?: Record<string, ReportJobNestedSectionCombineMode | undefined>;
  nestedSectionLayoutModeByGroupId?: Record<string, ReportJobNestedSectionLayoutMode | undefined>;
}

const REPORT_GROUP_WRAPPER_NAMES_TO_EXPAND = ['qa_analysis_results'];
const DEFAULT_MULTI_SUBSECTION_COMBINE_MODE: ReportJobNestedSectionCombineMode = 'unwound';
const DEFAULT_MULTI_SUBSECTION_LAYOUT_MODE: ReportJobNestedSectionLayoutMode = 'two-up';

const buildSingleResultGroup = (resultKey: string, result: RenderableReportJobResult): ReportJobResultGroup => ({
  [resultKey]: result,
});

const buildSortedEntries = <ResultType extends { index?: number }>(resultGroup: Record<string, ResultType>) =>
  sortBy(toPairs(resultGroup), ([resultKey, resultEntry]) => resultEntry?.index ?? resultKey);

const normalizeNestedSectionCombineMode = (
  combineMode?: ReportJobNestedSectionCombineMode
): ReportJobNestedSectionCombineMode => combineMode || DEFAULT_MULTI_SUBSECTION_COMBINE_MODE;

const normalizeNestedSectionLayoutMode = (
  layoutMode?: ReportJobNestedSectionLayoutMode
): ReportJobNestedSectionLayoutMode => layoutMode || DEFAULT_MULTI_SUBSECTION_LAYOUT_MODE;

const buildExpandedSectionsForWrapper = (
  resultGroupName: string,
  resultGroup: ReportJobResultGroup,
  nestedSectionCombineModeByGroupId: Record<string, ReportJobNestedSectionCombineMode | undefined>,
  nestedSectionLayoutModeByGroupId: Record<string, ReportJobNestedSectionLayoutMode | undefined>
) =>
  compact(
    flatMap(buildSortedEntries(resultGroup), ([resultKey, resultEntryOrNestedResultEntry]) => {
      if (!isNestedReportJobResult(resultEntryOrNestedResultEntry)) {
        return [
          {
            id: `${resultGroupName}:${resultKey}`,
            title: resultEntryOrNestedResultEntry.title || humanize(resultKey),
            resultGroup: buildSingleResultGroup(resultKey, resultEntryOrNestedResultEntry),
            sourceGroupName: resultGroupName,
            isExpandedFromWrapper: true,
          },
        ];
      }

      const nestedResults = resultEntryOrNestedResultEntry.results || {};
      const nestedResultEntries = buildSortedEntries(nestedResults);

      if (!nestedResultEntries.length) {
        return [];
      }

      const groupId = `${resultGroupName}:${resultKey}`;
      const combineMode = normalizeNestedSectionCombineMode(nestedSectionCombineModeByGroupId[groupId]);
      const layoutMode = normalizeNestedSectionLayoutMode(nestedSectionLayoutModeByGroupId[groupId]);
      const nestedSectionControl =
        nestedResultEntries.length > 1
          ? {
              groupId,
              combineMode,
              layoutMode,
            }
          : undefined;

      if (combineMode === 'combined' || nestedResultEntries.length <= 1) {
        return [
          {
            id: groupId,
            title: resultEntryOrNestedResultEntry.title || humanize(resultKey),
            resultGroup: {
              [resultKey]: resultEntryOrNestedResultEntry,
            },
            sourceGroupName: resultGroupName,
            isExpandedFromWrapper: true,
            nestedSectionControl,
          },
        ];
      }

      return nestedResultEntries.map(([nestedResultKey, nestedResultEntry]) => ({
        id: `${groupId}:${nestedResultKey}`,
        title: nestedResultEntry.title || humanize(nestedResultKey),
        resultGroup: buildSingleResultGroup(nestedResultKey, nestedResultEntry),
        sourceGroupName: resultGroupName,
        isExpandedFromWrapper: true,
        navigationParentId: groupId,
        navigationParentTitle: resultEntryOrNestedResultEntry.title || humanize(resultKey),
        rowGroupId: groupId,
        rowColumns: layoutMode === 'two-up' ? 2 : 1,
        nestedSectionControl,
      }));
    })
  );

export const buildReportJobDisplaySections = (
  reportJobResults?: ReportJobResults | null,
  {
    nestedSectionCombineModeByGroupId = {},
    nestedSectionLayoutModeByGroupId = {},
  }: BuildReportJobDisplaySectionsOptions = {}
): ReportJobDisplaySection[] => {
  return flatMap(toPairs(reportJobResults || {}), ([resultGroupName, resultGroup]) => {
    if (!REPORT_GROUP_WRAPPER_NAMES_TO_EXPAND.includes(resultGroupName)) {
      return [
        {
          id: resultGroupName,
          title: humanize(resultGroupName),
          resultGroup,
          sourceGroupName: resultGroupName,
          isExpandedFromWrapper: false,
        },
      ];
    }

    const expandedSections = buildExpandedSectionsForWrapper(
      resultGroupName,
      resultGroup,
      nestedSectionCombineModeByGroupId,
      nestedSectionLayoutModeByGroupId
    );

    return expandedSections.length
      ? expandedSections
      : [
          {
            id: resultGroupName,
            title: humanize(resultGroupName),
            resultGroup,
            sourceGroupName: resultGroupName,
            isExpandedFromWrapper: false,
          },
        ];
  });
};
